<template>
  <div class="v2-our-models">
    <div class="section-header mb-3 mb-md-4">
      <h2 id="meet-ersties" class="mb-0">
        {{ t("Meet the Ersties") }}
      </h2>
    </div>

    <template v-if="!models.length">
      <div class="row">
        <div v-for="n in 4" :key="n" class="col-6 col-md-3">
          <model-item-placeholder />
        </div>
      </div>
    </template>
    <template v-else>
      <transition-group name="fade" tag="div" class="row">
        <div v-for="(model, n) in topModels" :key="model.id" class="col-6 col-md-3">
          <model-item :model="model" :index="n" />
        </div>
      </transition-group>
      <div v-if="!otherModels.length" class="d-flex justify-content-center mt-2">
        <button class="btn btn-outline-light wide" @click="showMoreModels">
          {{ t("Meet more models") }}
        </button>
      </div>

      <transition-group v-if="otherModels.length" name="fade" tag="div" class="row other-models">
        <div v-for="(model, n) in otherModels" :key="model.id" class="col-6 col-md-3">
          <model-item :model="model" :index="n" theme="dark" />
        </div>
        <div key="button-dark" class="d-flex justify-content-center mt-2">
          <button class="btn btn-outline-dark wide" @click="showMoreModels">
            {{ t("Meet more models") }}
          </button>
        </div>
      </transition-group>
    </template>

    <span class="bg-sticker plug d-none d-lg-inline-block">
      <img v-lazy="require('@/assets/v2/images/stickers/plug.svg')" :alt="`sticker plug`" width="57" height="56" />
    </span>

    <span class="bg-sticker star-pink d-none d-md-inline-block">
      <img v-lazy="require('@/assets/v2/images/stickers/star-pink-light.svg')" :alt="`sticker star pink`" width="24" height="24" />
    </span>

    <span class="bg-sticker fingers d-none d-md-inline-block">
      <img v-lazy="require('@/assets/v2/images/stickers/fingers.svg')" :alt="`sticker fingers`" width="60" height="78" />
    </span>

    <span class="bg-sticker star-nude">
      <img v-lazy="require('@/assets/v2/images/stickers/star-nude-light.svg')" :alt="`sticker star nude`" width="16" height="16" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { onMounted, PropType, ref, watch } from "vue";
import { useModalStore } from "@/store/modal";
import ModelItem from "@/components/v2/model-item/index.vue";
import ModelItemPlaceholder from "@/components/v2/placeholders/model-item-placeholder/index.vue";
import { amplitudeButtonClickTrack } from "@/utils/amplitude";
import { Model } from "@/interfaces/Model/Model.ts";
import { useI18n } from "vue-i18n";

defineOptions({
  name: "our-models",
});

const props = defineProps({
  models: {
    type: Array as PropType<Model[]>,
    default: () => [],
  },
});

const modalStore = useModalStore();

const { t } = useI18n();

const batchSize = 4;
const topModels = ref<Model[]>([]);
const otherModels = ref<Model[]>([]);

watch(
  () => props.models,
  () => {
    topModels.value = props.models.slice(0, batchSize);
  },
  { deep: true },
);

const showMoreModels = () => {
  amplitudeButtonClickTrack("show more models");
  const loadedCount = topModels.value.length + otherModels.value.length;
  const limitReached = loadedCount >= props.models.length || loadedCount >= batchSize * 3;

  if (limitReached) {
    modalStore.showModal({ id: "modal-signup" });
    return;
  }

  otherModels.value = otherModels.value.concat(props.models.slice(loadedCount, loadedCount + batchSize));
};

onMounted(() => {
  topModels.value = props.models.slice(0, batchSize);
});
</script>

<style lang="scss">
@import "./index.scss";
</style>
